
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import type { FaqItem, FaqTableData } from '../types'
import { FAQ_CONSTRAINTS } from '../types'

/**
 * FaqCard component for displaying FAQ question and answer
 * Supports expand/collapse functionality with smooth animation
 * Supports text and table content with truncation
 * Supports mobile accordion mode with auto-scroll
 *
 * @see Requirements 6.1, 6.2, 6.3, 6.4, 6.5, 6.6, 6.7, 6.8
 * @see Requirements 7.1, 7.2, 7.3, 7.4 - Mobile accordion mode
 */
@Component
export default class FaqCard extends Vue {
  // === Props ===

  /** FAQ item data containing question and answer */
  @Prop({ type: Object, required: true }) readonly item!: FaqItem

  /** Display index (0-based) for showing Q number */
  @Prop({ type: Number, default: 0 }) readonly index!: number

  /** Initial expanded state (controlled by parent in accordion mode) */
  @Prop({ type: Boolean, default: false }) readonly expanded!: boolean

  /** Whether accordion mode is enabled (mobile) */
  @Prop({ type: Boolean, default: false }) readonly accordionMode!: boolean

  /** Whether the card is always open (PC mode, no toggle) */
  @Prop({ type: Boolean, default: false }) readonly alwaysOpen!: boolean

  /** Fixed header height offset for mobile scroll calculation */
  @Prop({ type: Number, default: 0 }) readonly scrollOffset!: number

  // === Data ===

  /** Current expanded state */
  isExpanded = false

  /** Reference to card element for scroll behavior */
  $refs!: {
    cardElement: HTMLDivElement;
    contentElement: HTMLDivElement;
  }

  // === Constants ===

  /** Maximum answer text length (500 characters per Requirement 8.4) */
  readonly MAX_ANSWER_LENGTH = FAQ_CONSTRAINTS.MAX_ANSWER_LENGTH

  /** Maximum table columns for PC (5 per Requirement 6.6) */
  readonly MAX_TABLE_COLUMNS = FAQ_CONSTRAINTS.MAX_TABLE_COLUMNS_PC

  /** Maximum table rows for PC (20 per Requirement 6.6) */
  readonly MAX_TABLE_ROWS = FAQ_CONSTRAINTS.MAX_TABLE_ROWS_PC

  // === Lifecycle ===

  created(): void {
    this.isExpanded = this.expanded
  }

  // === Watchers ===

  /**
   * Watch for external expanded state changes (accordion mode)
   * This allows parent component to control the expanded state
   */
  @Watch('expanded')
  onExpandedChange(newVal: boolean): void {
    if (this.isExpanded !== newVal) {
      this.isExpanded = newVal
    }
  }

  // === Computed ===

  /** Check if item has rich content blocks */
  get hasBlocks(): boolean {
    return Array.isArray(this.item.blocks) && this.item.blocks.length > 0
  }

  /** Check if item has text content */
  get hasTextContent(): boolean {
    return !!this.item.answer && this.item.answer.length > 0
  }

  /** Check if item has table content */
  get hasTableContent(): boolean {
    return !!this.item.table && 
      this.item.table.headers.length > 0 && 
      this.item.table.rows.length > 0
  }

  /** Get truncated answer text */
  get truncatedAnswer(): string {
    if (!this.item.answer) return ''
    if (this.item.answer.length <= this.MAX_ANSWER_LENGTH) {
      return this.item.answer
    }
    return this.item.answer.substring(0, this.MAX_ANSWER_LENGTH)
  }

  /** Check if answer text is truncated */
  get isAnswerTruncated(): boolean {
    return !!this.item.answer && this.item.answer.length > this.MAX_ANSWER_LENGTH
  }

  /** Get truncated table headers */
  get truncatedTableHeaders(): string[] {
    if (!this.item.table) return []
    return this.item.table.headers.slice(0, this.MAX_TABLE_COLUMNS)
  }

  /** Get truncated table rows */
  get truncatedTableRows(): string[][] {
    if (!this.item.table) return []
    return this.item.table.rows
      .slice(0, this.MAX_TABLE_ROWS)
      .map(row => row.slice(0, this.MAX_TABLE_COLUMNS))
  }

  /** Check if table content is truncated */
  get isTableTruncated(): boolean {
    if (!this.item.table) return false
    const table = this.item.table as FaqTableData
    return table.headers.length > this.MAX_TABLE_COLUMNS || 
      table.rows.length > this.MAX_TABLE_ROWS
  }

  // === Methods ===

  /**
   * Handle header click — no-op in alwaysOpen mode
   */
  handleHeaderClick(): void {
    if (this.alwaysOpen) return
    this.toggle()
  }

  /**
   * Toggle the expand/collapse state
   * Emits 'toggle' event with item id and new expanded state
   * In accordion mode, parent will manage other cards' collapse
   */
  toggle(): void {
    const newExpandedState = !this.isExpanded
    this.isExpanded = newExpandedState
    this.$emit('toggle', this.item.id, newExpandedState)
  }

  /**
   * Scroll card into view if bottom is outside viewport
   * Called after expansion animation completes
   * Per Requirement 7.3: scroll within 500ms if bottom outside viewport
   */
  private scrollIntoViewIfNeeded(): void {
    this.$nextTick(() => {
      const cardElement = this.$refs.cardElement
      if (!cardElement) return

      const rect = cardElement.getBoundingClientRect()
      const viewportHeight = window.innerHeight

      // Check if card bottom is outside viewport
      if (rect.bottom > viewportHeight) {
        const scrollOptions: ScrollToOptions = {
          top: rect.top + window.scrollY - this.scrollOffset - 20, // 20px buffer
          behavior: 'smooth'
        }
        window.scrollTo(scrollOptions)
      }
    })
  }

  // === Transition Hooks ===

  onEnter(element: HTMLElement): void {
    // Set initial height to 0 for animation start
    element.style.height = '0'
    element.style.overflow = 'hidden'
  }

  onAfterEnter(element: HTMLElement): void {
    // Animate to content height
    element.style.height = `${element.scrollHeight}px`
    // After animation, remove fixed height
    setTimeout(() => {
      element.style.height = 'auto'
      element.style.overflow = 'visible'

      // Scroll into view if needed (accordion mode, Requirement 7.3)
      if (this.accordionMode) {
        this.scrollIntoViewIfNeeded()
      }
    }, 300)
  }

  onLeave(element: HTMLElement): void {
    // Set fixed height for animation
    element.style.height = `${element.scrollHeight}px`
    // Force reflow
    element.offsetHeight
    // Animate to 0
    element.style.height = '0'
    element.style.overflow = 'hidden'
  }

  onAfterLeave(element: HTMLElement): void {
    element.style.height = ''
    element.style.overflow = ''
  }
}
